import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { TeamContact } from './models';
import { baseApi } from '@centerpiece/components';

export const team_contact = baseApi
  .enhanceEndpoints({ addTagTypes: ['TeamContacts', 'TeamContact'] })
  .injectEndpoints({
    endpoints: builder => ({
      getTeamContacts: builder.query<TeamContact[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'team_contact',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'TeamContact' as const,
                  id,
                })),
                'TeamContacts',
              ]
            : ['TeamContacts'],
      }),
      getTeamContact: builder.query<TeamContact, string>({
        query: id => ({
          url: `team_contact/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'TeamContact', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createTeamContact: builder.mutation({
        query: request => ({
          url: 'team_contact',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['TeamContacts'],
      }),
      updateTeamContact: builder.mutation<TeamContact, TeamContact>({
        query: request => ({
          url: `team_contact/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'TeamContact', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            team_contact.util.updateQueryData(
              'getTeamContact',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(team_contact.util.invalidateTags(['TeamContacts']))
             */
            dispatch(team_contact.util.invalidateTags(['TeamContacts']));
          }
        },
      }),
      deleteTeamContact: builder.mutation<TeamContact, number>({
        query: id => ({
          url: `team_contact/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'TeamContact' as const, arg }, 'TeamContacts']
            : ['TeamContacts'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetTeamContactsQuery,
  useGetTeamContactQuery,
  useCreateTeamContactMutation,
  useUpdateTeamContactMutation,
  useDeleteTeamContactMutation,
  usePrefetch: useTeamContactsPrefetch,
} = team_contact;
const selectTeamContactsRoot = team_contact.endpoints.getTeamContacts.select(
  {}
);
export const selectTeamContacts = createSelector(
  selectTeamContactsRoot,
  team_contact => team_contact.data || []
);
